import { useState } from 'react';
import MediaEmbed from '../components/MediaEmbed';
import { useIsMobile } from '../hooks/useIsMobile';

// Preferred display label + ordering for category filters.
// Any category present in the data gets a filter tab automatically,
// so nothing with a badge is ever unreachable.
const FILTER_LABELS = {
  reels: 'Social Reels',
  oncamera: 'On-Camera',
  consulting: 'Brand & Consulting',
  design: 'Brand & Design',
  writing: 'Writing',
  systems: 'Systems',
  longform: 'Long-Form Video',
};
const CATEGORY_ORDER = ['reels', 'oncamera', 'consulting', 'design', 'writing', 'systems', 'longform'];

function buildFilters(posts) {
  const present = [...new Set(posts.map(p => p.category).filter(Boolean))];
  const ordered = [
    ...CATEGORY_ORDER.filter(c => present.includes(c)),
    ...present.filter(c => !CATEGORY_ORDER.includes(c)),
  ];
  return [
    { id: 'all', label: 'All Work' },
    ...ordered.map(c => ({ id: c, label: FILTER_LABELS[c] || c.charAt(0).toUpperCase() + c.slice(1) })),
  ];
}

function interleaveByCategory(posts) {
  const groups = {};
  const order = [];
  posts.forEach(p => {
    if (!groups[p.category]) { groups[p.category] = []; order.push(p.category); }
    groups[p.category].push(p);
  });
  const cats = [...new Set(order)];
  const result = [];
  let i = 0, hasMore = true;
  while (hasMore) {
    hasMore = false;
    for (const cat of cats) {
      if (groups[cat][i]) { result.push(groups[cat][i]); hasMore = true; }
    }
    i++;
  }
  return result;
}

export default function Portfolio({ content }) {
  const isMobile = useIsMobile();
  const [filter, setFilter] = useState('all');
  const posts = content?.portfolio || [];
  const filters = buildFilters(posts);
  const filtered = filter === 'all' ? interleaveByCategory(posts) : posts.filter(p => p.category === filter);

  return (
    <div className="page-enter">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 900, fontStyle: 'italic', color: 'var(--ink)', marginBottom: 4 }}>
            Work <span style={{ color: 'var(--deep-rose)' }}>Portfolio</span>
          </div>
          <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 13, color: 'var(--text3)', letterSpacing: '.04em' }}>
            // Real work · real results · click any card to explore
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {filters.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            style={{ fontSize: 13, fontWeight: 700, padding: '5px 14px', borderRadius: 20, border: '1.5px solid', cursor: 'pointer', transition: 'all .15s', fontFamily: "'Nunito', sans-serif", borderColor: filter === f.id ? 'var(--crimson)' : 'var(--border2)', background: filter === f.id ? 'var(--crimson)' : 'white', color: filter === f.id ? 'white' : 'var(--text2)' }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 14 }}>
        {filtered.map(post => <WorkCard key={post.id} post={post} />)}
      </div>
    </div>
  );
}

const CATEGORY_META = {
  reels:      { color: 'rose',       label: 'Social Reels' },
  oncamera:   { color: 'periwinkle', label: 'On-Camera / VO' },
  longform:   { color: 'gold',       label: 'Long-Form Video' },
  systems:    { color: 'sage',       label: 'Systems' },
  consulting: { color: 'crimson',    label: 'Brand / Consulting' },
  design:     { color: 'periwinkle', label: 'Brand & Design' },
  writing:    { color: 'deep-gold',  label: 'Writing' },
  // legacy fallbacks
  video:      { color: 'rose',       label: 'Video' },
  social:     { color: 'sage',       label: 'Social' },
  broadcast:  { color: 'gold',       label: 'Broadcast' },
  brand:      { color: 'periwinkle', label: 'Brand' },
};

function embedHeight(post) {
  const url = post.mediaUrl || '';
  if (url.includes('instagram.com')) return 400;
  if (url.includes('tiktok.com')) return 500;
  if (url.includes('linkedin.com')) return 500;
  if (url.includes('facebook.com')) return 300;
  return 220;
}

// Confidential entry card — blurred preview + redaction notice
function ConfidentialCard({ post, meta }) {
  return (
    <div className="panel" style={{ borderRadius: 16, overflow: 'hidden', transition: 'all .22s', display: 'flex', flexDirection: 'column' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
    >
      {post.mediaUrl && (
        <div style={{ position: 'relative', overflow: 'hidden', maxHeight: 180 }}>
          <img src={post.mediaUrl} alt="confidential preview" style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(42,31,45,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11, letterSpacing: '.12em', color: 'rgba(255,255,255,.75)', textTransform: 'uppercase', background: 'rgba(0,0,0,.35)', padding: '4px 10px', borderRadius: 6 }}>Confidential</span>
          </div>
          <div style={{ position: 'absolute', top: 10, left: 10 }}>
            <span className={`chip chip-${meta.color}`} style={{ background: 'rgba(255,255,255,.18)', color: 'white', borderColor: 'rgba(255,255,255,.3)' }}>
              {post.badge || meta.label}
            </span>
          </div>
          {post.link && (
            <a href={post.link} target="_blank" rel="noopener noreferrer"
              style={{ position: 'absolute', top: 10, right: 10, fontSize: 11, fontFamily: "'Courier Prime', monospace", background: 'rgba(0,0,0,.5)', color: 'white', borderRadius: 8, padding: '3px 9px', textDecoration: 'none', letterSpacing: '.04em' }}>
              ↗ Client
            </a>
          )}
        </div>
      )}
      <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>{post.title}</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 10 }}>{post.desc}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
          <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text3)' }}>
            {post.platform}{post.date ? ` · ${post.date}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {post.stats?.filter(s => s.value).map((s, i) => (
              <span key={i} style={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 700, color: i === 0 ? 'var(--crimson)' : i === 1 ? 'var(--deep-sage)' : 'var(--periwinkle)' }}>
                {s.value} <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 10, fontWeight: 700, color: 'var(--text3)' }}>{s.label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
      <div style={{ padding: '8px 16px 12px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", letterSpacing: '.04em' }}>
          🔒 Client work · details available on request
        </span>
      </div>
    </div>
  );
}

function TextCard({ post, meta }) {
  const outLink = post.link || post.mediaUrl;
  return (
    <div className="panel" style={{ borderRadius: 16, overflow: 'hidden', transition: 'all .22s', display: 'flex', flexDirection: 'column' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
    >
      <div style={{ padding: '20px 20px 16px', background: `linear-gradient(135deg, var(--${meta.color === 'deep-gold' ? 'gold' : meta.color}) 0%, var(--ink) 140%)`, position: 'relative', minHeight: 90 }}>
        <span className={`chip chip-${meta.color}`} style={{ position: 'absolute', top: 12, left: 14, background: 'rgba(255,255,255,.18)', color: 'white', borderColor: 'rgba(255,255,255,.3)' }}>
          {post.badge || meta.label}
        </span>
        {outLink && (
          <a href={outLink} target="_blank" rel="noopener noreferrer"
            style={{ position: 'absolute', top: 12, right: 14, fontSize: 11, fontFamily: "'Courier Prime', monospace", background: 'rgba(0,0,0,.4)', color: 'white', borderRadius: 8, padding: '3px 9px', textDecoration: 'none', letterSpacing: '.04em' }}>
            ↗ Open
          </a>
        )}
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 900, fontStyle: 'italic', color: 'white', marginTop: 28, lineHeight: 1.25 }}>{post.title}</div>
      </div>
      <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 12 }}>{post.desc}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
          <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text3)' }}>
            {post.platform}{post.date ? ` · ${post.date}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {post.stats?.filter(s => s.value).map((s, i) => (
              <span key={i} style={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 700, color: i === 0 ? 'var(--crimson)' : i === 1 ? 'var(--deep-sage)' : 'var(--periwinkle)' }}>
                {s.value} <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 10, fontWeight: 700, color: 'var(--text3)' }}>{s.label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkCard({ post }) {
  const meta = CATEGORY_META[post.category] || CATEGORY_META.reels;
  if (post.confidential) {
    return <ConfidentialCard post={post} meta={meta} />;
  }
  const hasMedia = !!(post.mediaUrl || post.embedCode || post.thumbnail);
  const noEmbed = ['systems', 'consulting', 'writing'].includes(post.category) && !post.mediaUrl && !post.embedCode;
  if (noEmbed || (!hasMedia && ['writing', 'consulting'].includes(post.category))) {
    return <TextCard post={post} meta={meta} />;
  }
  const outLink = post.link || post.mediaUrl;
  return (
    <div className="panel" style={{ borderRadius: 16, overflow: 'hidden', transition: 'all .22s' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
    >
      <div style={{ position: 'relative' }}>
        <MediaEmbed
          url={post.mediaUrl}
          embedCode={post.embedCode}
          thumbnail={post.thumbnail}
          title={post.title}
          height={embedHeight(post)}
        />
        <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }}>
          <span className={`chip chip-${meta.color}`}>{post.badge || meta.label}</span>
        </div>
        {outLink && (
          <a href={outLink} target="_blank" rel="noopener noreferrer"
            style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, fontSize: 11, fontFamily: "'Courier Prime', monospace", background: 'rgba(0,0,0,.55)', color: 'white', borderRadius: 8, padding: '3px 9px', textDecoration: 'none', letterSpacing: '.04em' }}>
            ↗ Open
          </a>
        )}
      </div>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>{post.title}</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 10 }}>{post.desc}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
          <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text3)' }}>
            {post.platform}{post.date ? ` · ${post.date}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {post.stats?.filter(s => s.value).map((s, i) => (
              <span key={i} style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 700, color: i === 0 ? 'var(--crimson)' : i === 1 ? 'var(--deep-sage)' : 'var(--periwinkle)' }}>
                {s.value} <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 10, fontWeight: 700, color: 'var(--text3)' }}>{s.label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
